/**
 * Finance: receipts, bills, invoices, payments, allocations to enterprises, cattle and owners,
 * budgets, owner statements and accounting exports. Documents are core fin_docs; this module owns
 * how they are entered, settled, divided, reported and exported. Money is never shown to people
 * without finance access, anywhere.
 *
 * Other modules: read a document's money status through GET /api/m/finance/links?kind=<kind>&id=<id>
 * (or /links/status?kind=<kind>&ids=a,b) and create linked documents with POST /api/m/finance/docs
 * including `links: [{ kind: "event" | "job" | "stock_lot" | ..., id, role }]`. Types: shared/types.ts.
 */
import { defineModule } from "../../contract";
import { DEFAULT_CATEGORIES, DOC_KINDS, ENTERPRISE_KINDS, KIND_CHOICES, PAYMENT_METHODS } from "./shared/categories";
import { LABEL_MESSAGES } from "./shared/labels";

export * from "./shared/types";
export { DEFAULT_CATEGORIES, DOC_KINDS, ENTERPRISE_KINDS, KIND_CHOICES, PAYMENT_METHODS } from "./shared/categories";

/** Summary periods offered to people and to the agent. */
export const PERIODS = {
  this_month: "This month", last_month: "Last month", ytd: "This year so far", this_year: "This year", last_year: "Last year", last_12_months: "Last 12 months", all: "All time",
} as const;

export default defineModule({
  id: "finance",
  label: "Finances",
  description: "Receipts, bills, invoices, payments, allocations and owner statements.",
  ranchSections: [{
    id: "finances",
    label: "Finances",
    description: "Money in and out, bills to pay, receipts, reports and exports for your accountant.",
    icon: "money",
    group: "money",
    permission: "finance.read",
    keywords: ["money", "finance", "receipt", "receipts", "bill", "bills", "invoice", "expense", "expenses", "income", "payment", "pay", "profit", "loss", "p&l", "budget", "quickbooks", "xero", "accountant", "taxes", "schedule f", "owner statement", "cost per head"],
    keywordsEs: ["dinero", "finanzas", "recibo", "recibos", "factura", "facturas", "cuenta", "gasto", "gastos", "ingreso", "ingresos", "pago", "pagar", "ganancia", "pérdida", "presupuesto", "contador", "impuestos", "estado de cuenta", "costo por cabeza"],
  }],
  messages: [
    ...DEFAULT_CATEGORIES.map((c) => c.name),
    ...Object.values(ENTERPRISE_KINDS),
    ...Object.values(PAYMENT_METHODS),
    ...Object.values(DOC_KINDS).flatMap((k) => [k.label, k.short]),
    ...KIND_CHOICES.flatMap((k) => [k.label, k.hint]),
    ...Object.values(PERIODS),
    ...LABEL_MESSAGES,
    "Shares add up to {{sum}}%. They need to add up to 100%.",
    "A share must be more than 0% and at most 100%",
    "Pick who or what gets this share",
    "The same one is listed twice",
    "Unknown kind of share",
  ],
});
